/**
 * PracticeSettingsDrawer - Slide-out drawer for Practice page settings.
 * Contains auto-detect toggle and dice type selector.
 * @module PracticeSettingsDrawer
 */

import { Sparkles } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CaptureModeToggle, type CaptureMode } from '@/components/CaptureModeToggle';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { useIsLandscape } from '@/hooks/useIsLandscape';
import { cn } from '@/lib/utils';
import { DICE_TYPES } from '@/services/recognition';

type DiceType = (typeof DICE_TYPES)[number]['value'];

/**
 * Props for PracticeSettingsDrawer component.
 */
export interface PracticeSettingsDrawerProps {
  /** Whether drawer is open */
  open: boolean;
  /** Callback when open state changes */
  onOpenChange: (open: boolean) => void;
  /** Whether auto-detect mode is enabled */
  useAutoDetect: boolean;
  /** Callback to toggle auto-detect mode */
  onToggleAutoDetect: () => void;
  /** Currently selected dice type */
  selectedDie: DiceType;
  /** Callback when dice type is selected */
  onSelectDie: (die: DiceType) => void;
  /** Currently selected capture mode (TICKET-299) */
  captureMode: CaptureMode;
  /** Callback when capture mode is changed by the user (TICKET-299) */
  onCaptureModeChange: (mode: CaptureMode) => void;
}

/**
 * Slide-out drawer for Practice page settings.
 * Slides from bottom in portrait, right in landscape.
 */
export function PracticeSettingsDrawer({
  open,
  onOpenChange,
  useAutoDetect,
  onToggleAutoDetect,
  selectedDie,
  onSelectDie,
  captureMode,
  onCaptureModeChange
}: PracticeSettingsDrawerProps) {
  const isLandscape = useIsLandscape();

  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction={isLandscape ? 'right' : 'bottom'}>
      <DrawerContent
        direction={isLandscape ? 'right' : 'bottom'}
        className={cn(
          isLandscape ? 'w-72' : 'max-h-[60vh]'
        )}
      >
        <DrawerHeader>
          <DrawerTitle className="text-glow-subtle">Practice Settings</DrawerTitle>
        </DrawerHeader>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Capture mode toggle (TICKET-299) */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Capture Mode</label>
            <CaptureModeToggle
              value={captureMode}
              onChange={onCaptureModeChange}
            />
            <p className="text-xs text-muted-foreground">
              {captureMode === 'video'
                ? 'Tap to record a short clip of the roll'
                : 'Tap to capture a single photo of the dice'}
            </p>
          </div>

          {/* Auto-detect toggle */}
          <div className="space-y-2 pt-2 border-t border-border">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Detection Mode</label>
              {useAutoDetect && (
                <Badge variant="accent" className="text-xs">
                  Active
                </Badge>
              )}
            </div>
            <Button
              variant={useAutoDetect ? 'accent' : 'secondary-action'}
              className="w-full gap-2"
              onClick={() => {
                onToggleAutoDetect();
              }}
            >
              <Sparkles className="h-4 w-4" />
              Auto-Detect {useAutoDetect ? 'On' : 'Off'}
            </Button>
            <p className="text-xs text-muted-foreground">
              {useAutoDetect
                ? 'AI will identify the dice type automatically'
                : 'Select your dice type manually below'}
            </p>
          </div>

          {/* Dice type selector - only shown when auto-detect is off */}
          {!useAutoDetect && (
            <div className="space-y-2 pt-2 border-t border-border">
              <label className="text-sm font-medium">Dice Type</label>
              <div className="grid grid-cols-3 gap-2">
                {DICE_TYPES.map((die) => (
                  <Button
                    key={die.value}
                    variant={selectedDie === die.value ? 'accent' : 'secondary-action'}
                    size="sm"
                    onClick={() => {
                      onSelectDie(die.value);
                      onOpenChange(false);
                    }}
                    className={cn(
                      'font-mono',
                      selectedDie === die.value && 'ring-2 ring-ring'
                    )}
                  >
                    {die.label}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Currently selected: {selectedDie.toUpperCase()}
              </p>
            </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
